import { Component, DoCheck, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from "@angular/core";
import { ControlContainer, NgModelGroup } from "@angular/forms";
import { IColorPickerChangeEventData } from "@tylertech/forge";
import { PropertyMetadata } from "@tylertech/tyler-api-types";
import { AugmentedModelObject, ValueChangedParms } from "../../../../interfaces";
import { MetadataInputTypeHelperService, MetadataPropertyService, MetadataService } from "../../../../services";

interface IRGBA
{
    r: number;
    g: number;
    b: number;
    a: number;
}

@Component({
    selector: "metadata-input-color",
    templateUrl: "./metadata-input-color.tmpl.html",
    viewProviders: [{ provide: ControlContainer, useExisting: NgModelGroup }],
    standalone: false
})
export class MetadataInputColorComponent implements OnInit, OnChanges, DoCheck
{
    // not really readonly, but we should treat it as immutable within this component.
    @Input()
    readonly model: string;

    @Input()
    metadataModel: AugmentedModelObject; // model should be a field on metadataModel, FYI

    @Input()
    parent: AugmentedModelObject; // metadataModel should be a field on parent, FYI

    @Input()
    propertyMetadata: PropertyMetadata;

    @Input()
    viewOnly: boolean;

    @Input()
    compact: boolean;

    @Input()
    label: string;

    @Output()
    onValueChanged = new EventEmitter<ValueChangedParms>();

    @Input()
    hint: string;

    @Input()
    error: string;

    @ViewChild("popup") popup: any;

    @ViewChild("popupContent") popupContent: any;

    /** Accessible in the template */ isViewOnly: boolean;

    opening: boolean = false;

    holdFocus: boolean = false;

    private isInitialized: boolean;

    /**
     * Metadata's Recalculate on change functionality mutates the metadataModel in a way that will not trigger a binding change event,
     * so we need to have a way to do a simple check in `$doCheck` to determine if the `this.model` value was changed with out our notice.
     * This should only be set in `$onInit` and `onModelChanged`.
     */
    private unboundModel: string;

    private _valueModel: string;

    private _popupOpen: boolean = false;

    constructor(
        private readonly $metadataService: MetadataService,
        private readonly $metadataInputTypeHelperService: MetadataInputTypeHelperService,
        private readonly $metadataPropertyService: MetadataPropertyService
    )
    { }

    ngOnInit(): void
    {
        this.isInitialized = true;
        this.onModelChanged(this.model);
    }

    ngDoCheck(): void
    {
        if (!this.isInitialized)
        {
            return;
        }
        //const model = isNaN(this.model) ? 0 : this.model;
        if (this.unboundModel !== this.model)
        {
            this.onModelChanged(this.model);
        }
        this.isViewOnly =
            (this.viewOnly && !this.propertyMetadata.ViewOnlyEnabled) ||
            this.$metadataPropertyService.isViewOnly(this.propertyMetadata, this.metadataModel, this.parent) ||
            this.$metadataPropertyService.isBeingRecalculated(this.propertyMetadata, this.metadataModel);
    }

    ngOnChanges(_changes: SimpleChanges): void
    {
        if (!this.isInitialized)
        {
            return;
        }
        const changedKeys = Object.keys(_changes);
        for (const key of changedKeys)
        {
            switch (key)
            {
                case "model":
                    const modelChange = _changes[key];
                    this.onModelChanged(modelChange.currentValue);
                    break;
            }
        }
    }

    private onModelChanged(value: string): void
    {
        this.unboundModel = value;
        // if model changed, check if we need to update valueModel or selectedItem.
        if (value !== "#ffffff" && !value)
        {
            this.valueModel = null;
            return;
        }
        this.valueModel = value;
    }

    get popupOpen(): boolean
    {
        return this._popupOpen;
    }

    set popupOpen(value: boolean)
    {
        this._popupOpen = value;
    }

    get popupElementId(): string
    {
        return `colorpicker-popup-${this.name}`;
    }

    get valueModel(): string
    {
        return this._valueModel;
    }

    set valueModel(value: string)
    {
        this._valueModel = value;
    }

    hexToRGBA(hexValue: string): number
    {
        const r = parseInt("0x" + hexValue.substr(1, 2));
        const g = parseInt("0x" + hexValue.substr(3, 2));
        const b = parseInt("0x" + hexValue.substr(5, 2));
        const a = parseInt("0x" + hexValue.substr(7, 2));
        return b | (g << 8) | (r << 16) | (a << 24);
    }

    rgbaToNumber(rgba: IRGBA): number
    {
        return (rgba.r << 16) + (rgba.g << 8) + (rgba.b);
    }

    get name(): string
    {
        return this.propertyMetadata.Name;
    }

    fireValueChanged(color: IColorPickerChangeEventData): void
    {
        let newValue: string;
        if (color)
        {
            newValue = `#${color.hex}`;
        }
        else
        {
            newValue = "#ffffff";
        }
        if (this.model === newValue || !this.onValueChanged)
        {
            return;
        }
        this.onValueChanged.emit({ value: newValue });
    }

    openPopup(): void
    {
        this.opening = true;
        this.popup.open();
        this.opening = false;
        const self = this;
        requestAnimationFrame(() =>
        {
            const id = self.popup._elementRef.nativeElement.id;
            const popup: any = document.querySelector(`#${this.getPopupId(id)}`);
            const popupTarget: any = document.querySelector(`#${id}`);
            const targetBounds = popupTarget.getBoundingClientRect();
            if (popup)
                popup.style.width = `${targetBounds.width + 30}px`;
        });
    }

    focusOut($event: any): void
    {
        if (!$event.currentTarget.contains($event.relatedTarget))
        {
            this.closePopup();
            this.holdFocus = true;
        }
    }

    keyUp($event: any): void
    {
        if ($event.key === "Escape" && !$event.currentTarget.contains($event.relatedTarget))
        {
            this.closePopup();
            this.holdFocus = true;
        }
    }

    closePopup(): void
    {
        const target: any = document.querySelector(`#${this.getIdFromPopupId(this.popupContent.nativeElement.id)}`);
        target.focus();
        this.popup.close();
    }

    closeButtonKeyDown(e: KeyboardEvent): void
    {
        if (e.key === "Tab")
        {
            e.preventDefault();
            this.closePopup();
        }
    }

    private getIdFromPopupId(popupId): any
    {
        if (popupId.includes("color-picker-")) return popupId.replace("color-picker-", "clr-");
        else return popupId;
    }

    getPopupId(id): any
    {
        if (id.includes("clr-box-")) return id.replace("clr-box-", "color-picker-");
        else return id;
    }
}
